import React, { useState, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import DataTable, { useServerTable } from '../components/ui/DataTable';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import { ConfirmModal } from '../components/ui/Modal';
import Input, { Select } from '../components/ui/Input';
import Spinner from '../components/ui/Spinner';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { useAuth } from '../contexts/AuthContext';
import { dateFormat, useDocumentTitle } from '../config/utils';
import { useForm } from 'react-hook-form';
const ROLES = [
  { value: 'SUPER_ADMIN', label: 'Super Admin', variant: 'danger' },
  { value: 'SUPPORT',     label: 'Support',     variant: 'gold' },
  { value: 'VIEWER',      label: 'Viewer',      variant: 'muted' },
];

export default function Users() {
  useDocumentTitle('Team Members');
  const { user: me }   = useAuth();
  const { showToast }  = useGlobal();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting]     = useState(false);
  const [search, setSearch]         = useState('');

  const fetcher = useCallback(async ({ page, perPage }) => {
    const res = await adminApi.post(`/datatable/users/view?page=${page + 1}&per_page=${perPage}`, {
      order_by: { created_at: 'desc' },
      filters: { name: { value: search, matchMode: 'contains' } },
    });
    return res.data;
  }, [search]);

  const table = useServerTable(fetcher);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const onCreate = async (data) => {
    try {
      await adminApi.post('/users/create', data);
      showToast('success', 'Member Added', `${data.name} has been invited.`);
      setCreateOpen(false);
      reset();
      table.refresh();
    } catch (err) {
      showToast('error', 'Error', err?.response?.data?.message ?? 'Create failed.');
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.delete(`/datatable/users/${deleteUser.id}/delete`);
      showToast('success', 'Removed', `${deleteUser.name} has been removed.`);
      setDeleteUser(null);
      table.refresh();
    } catch {
      showToast('error', 'Error', 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const roleInfo = (role) => ROLES.find(r => r.value === role) ?? ROLES[2];

  const columns = [
    { key: 'name', header: 'Member', render: r => {
      const initials = (r.name || '').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';
      return (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold" style={{ background: '#696cff' }}>{initials}</div>
          <div>
            <p className="text-text-main text-sm font-medium">{r.name}</p>
            <p className="text-text-muted text-xs">{r.email}</p>
          </div>
        </div>
      );
    }},
    { key: 'phone',  header: 'Phone', render: r => r.phone ?? '—' },
    { key: 'type',   header: 'Role',  render: r => {
      const info = roleInfo(r.type);
      return <Badge variant={info.variant}>{info.label}</Badge>;
    }},
    { key: 'status', header: 'Status', render: r => <Badge variant="teal">{r.status?.toLowerCase()}</Badge> },
    { key: 'created_at', header: 'Joined', render: r => dateFormat(r.created_at) },
  ];

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="section-title text-xl">Team Members</h1>
          <p className="text-text-muted text-sm mt-0.5">{table.total} members</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="btn-primary">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          Add Member
        </button>
      </div>

      {/* Role legend */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {ROLES.map(r => <Badge key={r.value} variant={r.variant}>{r.label}</Badge>)}
      </div>

      <div className="mb-5">
        <Input placeholder="Search by name or email…" value={search}
          onChange={e => { setSearch(e.target.value); table.setPage(0); }}
          className="max-w-sm" />
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          {...table}
          onPageChange={table.setPage}
          onPerPageChange={table.setPerPage}
          actions={row => (
            row.email !== me?.email ? (
              <button
                onClick={() => setDeleteUser(row)}
                className="btn-icon text-danger hover:bg-danger/10"
                title="Remove member"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                </svg>
              </button>
            ) : null
          )}
        />
      </div>

      {/* Create Modal */}
      <Modal
        open={createOpen}
        onClose={() => { setCreateOpen(false); reset(); }}
        title="Add Team Member"
        footer={
          <>
            <button className="btn-secondary" onClick={() => { setCreateOpen(false); reset(); }}>Cancel</button>
            <button className="btn-primary" disabled={isSubmitting} onClick={handleSubmit(onCreate)}>
              {isSubmitting ? <Spinner size="sm" /> : 'Send Invite'}
            </button>
          </>
        }
      >
        <form className="space-y-4">
          <Input label="Full Name" placeholder="John Doe" error={errors.name?.message}
            {...register('name', { required: 'Name is required' })} />
          <Input label="Email Address" type="email" placeholder="john@company.com" error={errors.email?.message}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' },
            })} />
          <Select label="Role" {...register('type')} error={errors.type?.message}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>
        </form>
      </Modal>

      <ConfirmModal
        open={!!deleteUser}
        onClose={() => setDeleteUser(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Remove Member"
        message={`Remove "${deleteUser?.name}"? They will lose access immediately.`}
      />
    </Layout>
  );
}
